import { useAxios } from 'hooks/useAxios';
import config from '../../config';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addCvProject,
  addToCvProject,
  approveAllCvProfiles,
  approveCvProject,
  exportCsvFromCVProject,
  getCvProjectById,
  getCvProjects,
  getRequestedCvProjects,
  notifyUploadIsFinished,
  removeCvProject,
  updateCvProject
} from 'services/api/cv-projects';
import { CsvExportOptionsEnum, ICVProject } from '@recone/models';
import useTeamStore from 'store/useTeamStore';

const GET_CV_PROJECTS_QUERY_ID = 'cv-projects';
const GET_REQUESTED_CV_PROJECTS_ID = 'req-cv-projects';
const GET_CV_PROFILE_QUERY_ID = 'cv-profiles';

export function useGetCvProjects(limit = config.service.QUERY_DEFAULT_LIMIT) {
  const axios = useAxios();
  const teamId = useTeamStore((state) => state.teamId);
  return useInfiniteQuery(
    [GET_CV_PROJECTS_QUERY_ID, teamId],
    ({ pageParam }) => getCvProjects(axios, pageParam, limit),
    {
      getNextPageParam: (lastData, pages) => pages.length
    }
  );
}

export const useGetSingleCvProject = (id: string) => {
  const axios = useAxios();
  return useQuery([GET_CV_PROJECTS_QUERY_ID, id], () => getCvProjectById(axios, id), {
    enabled: !!id
  });
};

export const useAddCvProject = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { name: string; description: string }) => addCvProject(axios, args.name, args.description),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_REQUESTED_CV_PROJECTS_ID]).then();
      }
    }
  );
};

export const useAddToCvProject = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { file: File; projectId: string }) => addToCvProject(axios, args.file, args.projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_REQUESTED_CV_PROJECTS_ID]).then();
    }
  });
};

export function useGetRequestedCvProjects(limit = config.service.QUERY_DEFAULT_LIMIT) {
  const axios = useAxios();
  const teamId = useTeamStore((state) => state.teamId);
  return useInfiniteQuery(
    [GET_REQUESTED_CV_PROJECTS_ID, teamId],
    ({ pageParam }) => getRequestedCvProjects(axios, pageParam, limit),
    {
      getNextPageParam: (lastData, pages) => pages.length
    }
  );
}

export const useApproveCvProject = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { projectId: string }) => approveCvProject(axios, args.projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_CV_PROJECTS_QUERY_ID, GET_REQUESTED_CV_PROJECTS_ID]).then();
    }
  });
};

export const useApproveAllCvProfiles = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { projectId: string }) => approveAllCvProfiles(axios, args.projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_CV_PROFILE_QUERY_ID, GET_REQUESTED_CV_PROJECTS_ID]).then();
    }
  });
};

export const useNotifyUploadIsFinished = () => {
  const axios = useAxios();

  return useMutation((args: { projectId: string }) => notifyUploadIsFinished(axios, args.projectId));
};

export const useDeleteCvProject = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((args: { projectId: string }) => removeCvProject(axios, args.projectId), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_REQUESTED_CV_PROJECTS_ID, GET_CV_PROJECTS_QUERY_ID]).then();
    }
  });
};

export const useUpdateCvProject = () => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation((cvProject: ICVProject) => updateCvProject(axios, cvProject), {
    onSuccess: () => {
      queryClient.invalidateQueries([GET_CV_PROJECTS_QUERY_ID, GET_REQUESTED_CV_PROJECTS_ID]).then();
    }
  });
};

export function useExportCsvFromCvProject() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation(
    (args: { id: string; exportOptions: CsvExportOptionsEnum }) =>
      exportCsvFromCVProject(axios, args.id, args.exportOptions),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_CV_PROJECTS_QUERY_ID]).then();
      }
    }
  );
}
